import { createContext, useContext, useId } from 'react';
import { cx } from '../internal/cx';

/** Props a Field hands to its control so the label, hint and error are wired up. */
export interface FieldControlProps {
  id: string;
  'aria-describedby'?: string | undefined;
  'aria-invalid'?: true | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
}

export interface FieldContextValue {
  id: string;
  labelId: string;
  describedBy: string | undefined;
  invalid: boolean;
  required: boolean;
  disabled: boolean;
}

export const FieldContext = createContext<FieldContextValue | null>(null);

export function useFieldContext(): FieldContextValue | null {
  return useContext(FieldContext);
}

interface OwnFieldProps {
  id?: string | undefined;
  'aria-describedby'?: string | undefined;
  invalid?: boolean | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
}

/**
 * Resolves id / aria-describedby / invalid / required / disabled for a form control, merging
 * its own props with the surrounding <Field> (own props win).
 */
export function useFieldProps(own: OwnFieldProps) {
  const field = useContext(FieldContext);
  const generatedId = useId();
  const invalid = own.invalid ?? field?.invalid ?? false;
  return {
    id: own.id ?? field?.id ?? generatedId,
    labelId: field?.labelId,
    describedBy: cx(field?.describedBy, own['aria-describedby']) || undefined,
    invalid,
    required: own.required ?? field?.required ?? false,
    disabled: own.disabled ?? field?.disabled ?? false,
  };
}
